import { useEffect, useState } from 'react';
import '../App.scss';
import { Button } from '@mui/base/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

import { AdminClient, UserModel, CompanyModel, UserClient } from '../types';
import { createClient } from '../api/clients';
import UserTable from '../components/userTable';


function Admin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [companies, setCompanies] = useState<CompanyModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const [userToDelete, setUserToDelete] = useState<UserModel | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const adminClient = createClient(AdminClient);
  const userClient = createClient(UserClient);

  useEffect(() => {
    const state = location.state as { message?: string } | null;

    if (state?.message && state.message.trim() !== '') {
      setInfoMessage(state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const fetchCompanies = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const result: CompanyModel[] = await adminClient.getUsers();
      setCompanies(result);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleCreateUser = () => {
    navigate('/registration');
  };

  const handleCreateCompany = () => {
    navigate('/company/create');
  };

  const handleEdit = (id?: string) => {
    if (!id) return;
    
    navigate(`/team-management/edit/${id}`, {
        state: { returnTo: '/admin' }
    });
  };

  const handleEditCompany = (id?: string) => {
    if (!id) {
      return;
    }
    navigate(`/company/${id}`);
  };


  const handleDeleteClick = (user: UserModel) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
    setErrorMessage(null);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };


 const handleBackToProjects = () => {
    navigate('/project-admin');
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete?.id) return;

    try {
      const result = await userClient.deleteUser(userToDelete.id);

      if (result.success === false) {
        setErrorMessage(result.error ?? 'An error occurred while deleting the user. Please try again.');
        return;
      }

      await fetchCompanies();
      setInfoMessage('User deleted successfully.');
    } catch (err) {
      console.error('Error deleting user:', err);
      setErrorMessage('An error occurred while deleting the user. Please try again.');
    } finally {
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

return (
  <div className="main container py-4 mb-6">
    <div
      className="mx-auto"
      style={{
        maxWidth: "1000px",
        paddingLeft: "1rem",
        paddingRight: "1rem",
      }}
    >
      <div className="mb-3">
          <Button className="styled-button" onClick={handleBackToProjects} id="btn-projects">
            Back to projects
          </Button>
      </div>

    <div className="d-flex flex-column align-items-end">
      <div className="mb-3">
        <Button className="styled-button" onClick={handleCreateUser} id="btn-add-user">
          Add User
        </Button>
      </div>

      <div className="mb-3">
        <Button className="styled-button" onClick={handleCreateCompany} id="btn-add-company">
          Add Company
        </Button>
      </div>
    </div>

       <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">Admin – all organisations</h2>
    </div>

      <p className="mb-3">
        View and manage users across all companies.
      </p>

      {infoMessage && (
        <p style={{ color: "green", marginBottom: "1rem" }}>
          {infoMessage}
        </p>
      )}

      {loading && <p>Loading users…</p>}

      {errorMessage && !loading && (
        <div className="form-error mb-3">
          {errorMessage}
        </div>
      )}

      {!loading && !errorMessage && (
        <>
          {companies.length === 0 ? (
            <p>No companies found.</p>
          ) : (
            <div style={{ width: "100%" }}>
              {companies.map((company) => (
               <div key={company.name} style={{ marginBottom: "3em" }}>
                  <div className="d-flex justify-content-between align-items-baseline mb-1">
                    <h4 className="mb-0">{company.name}  ({company.projectCount} / {company.projectLimit} projects used) {company.isEnabled ? "" : "- Disabled"} </h4>
                   
                    <Button
                      className="styled-button"
                      onClick={() => handleEditCompany(company.id)}
                    >
                      Edit Company
                    </Button>
                  </div>

                  {(!company || !company.users || company.users.length === 0) ? (
                    <p>No users found for this company.</p>
                  ) : (
                    <UserTable
                      users={company.users}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>

    <Dialog
      open={isDeleteDialogOpen}
      onClose={handleCloseDeleteDialog}
    >
      <DialogTitle>Delete user</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`Are you sure you want to delete ${
            userToDelete?.name ? `"${userToDelete.name}"` : "this user"
          }? This action cannot be undone.`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button className="styled-button" onClick={handleCloseDeleteDialog}>
          Cancel
        </Button>
        <Button className="styled-button" onClick={handleConfirmDelete}>
          Yes, delete
        </Button>
      </DialogActions>
    </Dialog>
  </div>
);

}

export default Admin;
